/**
 * Company settings validation schema
 * Validates company data with Dutch-specific rules
 */

import * as Yup from 'yup'
import { FORM_LABELS, LABELS } from '@constants'
import { createNLPostalCodeValidator, createNLKVKValidator, createEmailValidator } from '@utilities/validation'

/**
 * Company settings validation schema
 *
 * Note: Complex async validations (IBAN, BTW-ID) are handled separately in the form components
 * using react-hook-form's rules prop, as they require API calls and auto-fill logic.
 * This schema covers the simpler field validations.
 */
export const myCompanyValidationSchema = () =>
  Yup.object().shape({
    company: Yup.object().shape({
      // General company info
      name: Yup.string()
        .required(FORM_LABELS.REQUIRED(LABELS.COMPANY_NAME))
        .max(255, FORM_LABELS.MAX_LENGTH(LABELS.COMPANY_NAME, 255)),

      email: createEmailValidator(FORM_LABELS.INVALID_EMAIL)
        .nullable()
        .default('')
        .max(255, FORM_LABELS.MAX_LENGTH(LABELS.EMAIL, 255)),

      phone: Yup.string().nullable().default('').max(20, FORM_LABELS.MAX_LENGTH('Telefoonnummer', 20)),

      // KVK number - Dutch format
      coc: createNLKVKValidator(FORM_LABELS.INVALID_KVK_NUMBER).default('').nullable(),

      // Tax fields - tax is validated in the component with async validation
      tax_payable: Yup.boolean().default(false),
      uses_kor: Yup.boolean().default(false),

      // Bank account fields
      bank_account_holder_name: Yup.string()
        .nullable()
        .default('')
        .max(70, FORM_LABELS.MAX_LENGTH('Tenaamstelling', 70)),

      // QR code settings
      qr_enabled: Yup.boolean().default(true),

      // Address fields - required for invoices
      street: Yup.string()
        .required(FORM_LABELS.REQUIRED(LABELS.STREET))
        .max(255, FORM_LABELS.MAX_LENGTH(LABELS.STREET, 255)),

      house_number: Yup.string()
        .required(FORM_LABELS.REQUIRED(LABELS.HOUSE_NUMBER))
        .max(20, FORM_LABELS.MAX_LENGTH(LABELS.HOUSE_NUMBER, 20)),

      house_number_addition: Yup.string()
        .nullable()
        .max(20, FORM_LABELS.MAX_LENGTH(LABELS.HOUSE_NUMBER_ADDITION, 20)),

      postal_code: createNLPostalCodeValidator(FORM_LABELS.INVALID_POSTAL_CODE_NL)
        .required(FORM_LABELS.REQUIRED(LABELS.POSTAL_CODE)),

      city: Yup.string()
        .required(FORM_LABELS.REQUIRED(LABELS.CITY))
        .max(255, FORM_LABELS.MAX_LENGTH(LABELS.CITY, 255)),

      // Invoice/Quotation settings
      hourly_rate: Yup.number()
        .nullable()
        .min(0, FORM_LABELS.HOURLY_RATE_NEGATIVE)
        .integer(FORM_LABELS.HOURLY_RATE_INTEGER),

      invoice_expires_after: Yup.number()
        .nullable()
        .min(0, 'Aantal dagen kan niet negatief zijn')
        .integer('Aantal dagen moet een heel getal zijn'),

      quotation_expires_after: Yup.number()
        .nullable()
        .min(0, 'Aantal dagen kan niet negatief zijn')
        .integer('Aantal dagen moet een heel getal zijn'),

      // SMTP email settings
      smtp_host: Yup.string().nullable().default('').max(255, FORM_LABELS.MAX_LENGTH('SMTP Host', 255)),

      smtp_port: Yup.number()
        .nullable()
        .min(1, 'Poortnummer moet minimaal 1 zijn')
        .max(65535, 'Poortnummer kan niet hoger dan 65535 zijn')
        .integer('Poortnummer moet een heel getal zijn'),

      smtp_username: createEmailValidator(FORM_LABELS.INVALID_EMAIL)
        .nullable()
        .default('')
        .max(255, FORM_LABELS.MAX_LENGTH('SMTP Gebruikersnaam', 255)),

      smtp_password: Yup.string().nullable().default('').max(255, FORM_LABELS.MAX_LENGTH('SMTP Wachtwoord', 255)),

      smtp_encryption: Yup.string()
        .nullable()
        .default('')
        .oneOf(['', 'tls', 'ssl', null], 'Versleuteling moet TLS, SSL of geen zijn'),

      smtp_from_address: createEmailValidator(FORM_LABELS.INVALID_EMAIL)
        .nullable()
        .default('')
        .max(255, FORM_LABELS.MAX_LENGTH('Afzender e-mailadres', 255)),

      smtp_from_name: Yup.string().nullable().default('').max(255, FORM_LABELS.MAX_LENGTH('Afzender naam', 255)),

      // Document number format settings
      settings: Yup.object().shape({
        invoice: Yup.object().shape({
          nr_format: Yup.string().default('[year][number]').max(50, 'Format mag maximaal 50 tekens bevatten'),

          nr_start: Yup.number()
            .nullable()
            .min(1, 'Startnummer moet minimaal 1 zijn')
            .integer('Startnummer moet een heel getal zijn'),
        }),

        quotation: Yup.object().shape({
          nr_format: Yup.string().default('[year][number]').max(50, 'Format mag maximaal 50 tekens bevatten'),

          nr_start: Yup.number()
            .nullable()
            .min(1, 'Startnummer moet minimaal 1 zijn')
            .integer('Startnummer moet een heel getal zijn'),
        }),
      }),
    }),
  })
